import { apiCall } from '../utils/api';

/**
 * Service für die Match-Runden ("Deine 6 für heute").
 *
 * Gegenstück zu backend/src/api/v1/routes/matchRounds.js. Die alte Endlosliste
 * (/profile/discover über useProfileLoader) bleibt unberührt daneben bestehen.
 */
const MatchRoundService = {
  /**
   * Holt die aktuelle Runde. Verbraucht bewusst KEIN Kontingent — Neuladen und
   * App-Wechsel dürfen nicht eine der drei Tagesrunden kosten.
   *
   * @returns {Promise<{profiles: Array, round: Object, meta: Object}>}
   */
  getRound: async () => {
    const response = await apiCall('/api/v1/match/round');
    return response?.data || { profiles: [], round: null, meta: {} };
  },

  /**
   * Zieht eine neue Runde und verbraucht eine vom Tageskontingent.
   *
   * Bei erschöpftem Kontingent antwortet der Server mit 429 und liefert den
   * Zustandsblock mit — deshalb wird der Fehler hier nicht verschluckt, sondern
   * mit `limitReached` und `round` angereichert weitergereicht.
   *
   * @returns {Promise<{profiles: Array, round: Object, meta: Object}>}
   */
  drawNextRound: async () => {
    try {
      const response = await apiCall('/api/v1/match/round/next', { method: 'POST' });
      return response?.data || { profiles: [], round: null, meta: {} };
    } catch (error) {
      // apiCall wirft einen Error mit message/code/status/details als
      // Eigenschaften, aber kein response-Feld. Deshalb kommt der Zustandsblock
      // über `details`, das der Helfer als einziges Nutzlast-Feld durchreicht.
      if (error?.status === 429) {
        const limitError = new Error('ROUND_LIMIT_REACHED');
        limitError.limitReached = true;
        limitError.round = error.details?.round || null;
        throw limitError;
      }
      if (error?.status === 409) {
        // Zwei Anfragen gleichzeitig — die andere hat gewonnen.
        const conflictError = new Error('ROUND_CONFLICT');
        conflictError.conflict = true;
        throw conflictError;
      }
      throw error;
    }
  },

  /**
   * "Später zeigen" — blendet ein Profil vorübergehend aus.
   * Keine Ablehnung: unter Klassenkamerad:innen ist ein "Nein" eine soziale
   * Aussage, ein "gerade nicht" nicht.
   *
   * @param {string} targetUserId
   * @param {number} [days] - 1 bis 90; ohne Angabe gilt die Server-Vorgabe
   */
  snooze: async (targetUserId, days) => {
    const body = days ? { targetUserId, days } : { targetUserId };
    const response = await apiCall('/api/v1/match/snooze', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response?.data || null;
  },

  /**
   * Nimmt eine Ausblendung zurück.
   * @param {string} targetUserId
   */
  unsnooze: async (targetUserId) => {
    const response = await apiCall(`/api/v1/match/snooze/${targetUserId}`, { method: 'DELETE' });
    return response?.data || null;
  },
};

export default MatchRoundService;
